import {
  AfterViewInit,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
} from '@angular/core';

const MASKED_INPUT_CLASS = 'vault-admin-masked-input';

/** Aplica anti-autofill al <input> interno de lib-input (ui-kit no expone estos atributos). */
@Directive({
  selector:
    'lib-input[vaultInputAutocomplete], lib-input[vaultInputName], lib-input[vaultInputReadonlyUntilFocus], lib-input[vaultInputMaskAsPassword], lib-input[vaultInputInputMode], lib-input[vaultInputRevealPassword]',
  standalone: true,
})
export class AdminUsersInputAutocompleteDirective implements AfterViewInit, OnDestroy {
  readonly vaultInputAutocomplete = input('');
  readonly vaultInputName = input('');
  readonly vaultInputInputMode = input('');
  readonly vaultInputRevealPassword = input(false);
  readonly vaultInputReadonlyUntilFocus = input(false, {
    transform: (v: unknown) => v === '' || v === true || v === 'true',
  });
  readonly vaultInputIgnorePasswordManagers = input(false, {
    transform: (v: unknown) => v === '' || v === true || v === 'true',
  });
  readonly vaultInputMaskAsPassword = input(false, {
    transform: (v: unknown) => v === '' || v === true || v === 'true',
  });

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly timeouts: number[] = [];
  private readonly teardown: (() => void)[] = [];

  constructor() {
    effect(() => {
      this.vaultInputRevealPassword();
      this.vaultInputMaskAsPassword();
      queueMicrotask(() => this.applyToInput());
    });
  }

  ngAfterViewInit(): void {
    this.applyToInput();
    for (const delay of [0, 50, 200, 500, 1000]) {
      this.timeouts.push(window.setTimeout(() => this.applyToInput(), delay));
    }
  }

  ngOnDestroy(): void {
    this.timeouts.forEach((id) => window.clearTimeout(id));
    this.teardown.forEach((fn) => fn());
  }

  private applyToInput(): void {
    const input = this.host.nativeElement.querySelector('input');
    if (!input) return;

    if (this.vaultInputMaskAsPassword()) {
      input.type = 'text';
      input.setAttribute('autocomplete', 'one-time-code');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');
      input.setAttribute('aria-autocomplete', 'none');
      input.removeAttribute('id');
      if (this.vaultInputRevealPassword()) {
        input.classList.remove(MASKED_INPUT_CLASS);
      } else {
        input.classList.add(MASKED_INPUT_CLASS);
      }
    } else {
      input.classList.remove(MASKED_INPUT_CLASS);
      const autocomplete = this.vaultInputAutocomplete();
      if (autocomplete) {
        input.setAttribute('autocomplete', autocomplete);
      }
    }

    const inputMode = this.vaultInputInputMode();
    if (inputMode) {
      input.setAttribute('inputmode', inputMode);
    } else if (this.vaultInputIgnorePasswordManagers()) {
      input.removeAttribute('inputmode');
    }

    const name = this.vaultInputName();
    if (name) {
      input.setAttribute('name', name);
      input.removeAttribute('id');
    }

    if (this.vaultInputIgnorePasswordManagers() || this.vaultInputMaskAsPassword()) {
      input.setAttribute('data-lpignore', 'true');
      input.setAttribute('data-1p-ignore', 'true');
      input.setAttribute('data-bwignore', 'true');
      input.setAttribute('data-form-type', 'other');
    }

    this.applyReadonlyUntilInteraction(input);
  }

  private applyReadonlyUntilInteraction(input: HTMLInputElement): void {
    if (!this.vaultInputReadonlyUntilFocus() || input.dataset['vaultFocusReady'] === 'true') {
      return;
    }

    input.setAttribute('readonly', 'true');
    const unlock = () => {
      input.removeAttribute('readonly');
      input.dataset['vaultFocusReady'] = 'true';
    };

    input.addEventListener('mousedown', unlock, { once: true });
    input.addEventListener('touchstart', unlock, { once: true, passive: true });
    input.addEventListener('focus', unlock, { once: true });

    this.teardown.push(() => {
      input.removeEventListener('mousedown', unlock);
      input.removeEventListener('touchstart', unlock);
      input.removeEventListener('focus', unlock);
    });
  }
}
